import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useControllerSoDVerified } from '../../../hooks/analytics/useControllerReports.ts';
import { RefreshMeta } from './shared.tsx';
import type { ControllerSoDVerifiedRow } from '../../../request/analytics/controllerReports.ts';

/** Report #27 — Segregation of Duties Verified. Rows with segregation_verified=false render red (auditor export candidate). */
export const SoDVerifiedTable: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerSoDVerified();

  const columns = useMemo<TableColumnDef<ControllerSoDVerifiedRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">
            {row.original.control_id} — {row.original.title}
          </span>
        ),
        widthPercent: 34,
      },
      {
        id: 'preparer_name',
        columnLabel: 'Preparer',
        header: () => 'Preparer',
        cell: ({ row }) => row.original.preparer_name ?? '—',
        widthPercent: 20,
      },
      {
        id: 'reviewer_name',
        columnLabel: 'Reviewer',
        header: () => 'Reviewer',
        cell: ({ row }) => row.original.reviewer_name ?? '—',
        widthPercent: 20,
      },
      {
        id: 'segregation_verified',
        columnLabel: 'SoD OK',
        header: () => 'SoD OK',
        cell: ({ row }) => {
          const verified = row.original.segregation_verified;
          return (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                verified === true
                  ? 'bg-green-100 text-green-700'
                  : verified === false
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {verified === true ? 'Verified' : verified === false ? 'Not verified' : 'N/A'}
            </span>
          );
        },
        widthPercent: 26,
      },
    ],
    []
  );

  const sortedData = useMemo(() => {
    const base = controlIdFilter ? (data ?? []).filter((r) => controlIdFilter.has(r.control_id)) : data ?? [];
    const rank = (v: boolean | null) => (v === false ? 0 : v == null ? 1 : 2);
    return [...base].sort((a, b) => rank(a.segregation_verified) - rank(b.segregation_verified));
  }, [data, controlIdFilter]);

  return (
    <AppDataTable<ControllerSoDVerifiedRow>
      data={sortedData}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load segregation of duties report.' : null}
      emptyMessage="No controls found."
      searchable
      searchPlaceholder="Search controls..."
      paginated
      defaultPageSize={10}
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default SoDVerifiedTable;
